import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Coins,
  History,
  Info,
  Lock,
  Minus,
  Plus,
  RefreshCw,
  Sparkles,
  Unlock,
} from "lucide-react";
import type { SuperAdminOrganizationMessageBalanceResponse } from "@multi-chat/shared";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../components/ui/tooltip/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";
import {
  getSuperAdminOrganizationMessageBalance,
  updateSuperAdminOrganizationMessageBalance,
} from "../../api/super-admin-api.js";
import { superAdminInputClassName } from "./super-admin-setting-rows.js";

interface SuperAdminMessageBalanceSectionProps {
  organizationId: number;
}

type ActionModal =
  | { type: "ADD" }
  | { type: "DEDUCT" }
  | { type: "SET_LIMITED" }
  | { type: "SET_UNLIMITED" }
  | null;

const QUICK_ADD_AMOUNTS = ["50", "100", "500", "1000"] as const;
const QUICK_INITIAL_AMOUNTS = ["0", "100", "500", "1000"] as const;

export function formatBigIntCredits(val: string | number | bigint, locale: string): string {
  try {
    const bi = typeof val === "bigint" ? val : BigInt(val);
    return new Intl.NumberFormat(locale).format(bi);
  } catch {
    return String(val);
  }
}

function parseSafeNonNegativeBigInt(raw: string): bigint | null {
  const trimmed = raw.trim();
  if (!/^(?:0|[1-9][0-9]*)$/.test(trimmed)) {
    return null;
  }
  try {
    const value = BigInt(trimmed);
    return value >= 0n ? value : null;
  } catch {
    return null;
  }
}

function BalanceInfoTooltip({
  label,
  content,
  testId,
}: {
  label: string;
  content: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent/60"
          aria-label={label}
          data-testid={testId}
        >
          <Info size={13} aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs px-3 py-2 text-start text-[0.75rem] font-normal leading-relaxed">
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

export function SuperAdminMessageBalanceSection({
  organizationId,
}: SuperAdminMessageBalanceSectionProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const notifications = useOptionalNotifications();

  const [balance, setBalance] = useState<SuperAdminOrganizationMessageBalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<ActionModal>(null);
  const [modalAmount, setModalAmount] = useState<string>("100");
  const [modalError, setModalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function loadBalance(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const data = await getSuperAdminOrganizationMessageBalance(organizationId);
      setBalance(data);
    } catch (err: any) {
      if (err?.code === "MESSAGE_BALANCE_NOT_CONFIGURED" || err?.status === 503) {
        setError(t("superAdmin.messageBalance.notConfigured"));
      } else {
        setError(t("superAdmin.messageBalance.loadError"));
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadBalance();
  }, [organizationId]);

  function handleOpenModal(type: ActionModal): void {
    setActiveModal(type);
    setModalAmount(
      type?.type === "ADD"
        ? "100"
        : type?.type === "DEDUCT"
          ? "10"
          : type?.type === "SET_LIMITED"
            ? "100"
            : "0",
    );
    setModalError(null);
  }

  function handleCloseModal(): void {
    if (submitting) return;
    setActiveModal(null);
    setModalError(null);
  }

  async function handleConfirmAction(): Promise<void> {
    if (!activeModal || !balance) return;
    setSubmitting(true);
    setModalError(null);

    try {
      let updated: SuperAdminOrganizationMessageBalanceResponse;

      if (activeModal.type === "ADD") {
        const amt = parseSafeNonNegativeBigInt(modalAmount);
        if (amt === null || amt <= 0n) {
          setModalError(t("superAdmin.messageBalance.errors.amountMustBePositive"));
          setSubmitting(false);
          return;
        }
        updated = await updateSuperAdminOrganizationMessageBalance(organizationId, {
          operation: "ADD",
          amount: amt.toString(),
          version: balance.version,
        });
        notifications?.pushNotification({
          kind: "success",
          message: t("superAdmin.messageBalance.toasts.addSuccess", {
            count: formatBigIntCredits(amt, i18n.language),
          }),
        });
      } else if (activeModal.type === "DEDUCT") {
        const amt = parseSafeNonNegativeBigInt(modalAmount);
        if (amt === null || amt <= 0n) {
          setModalError(t("superAdmin.messageBalance.errors.amountMustBePositive"));
          setSubmitting(false);
          return;
        }
        if (!balance.isUnlimited && amt > BigInt(balance.availableCredits)) {
          setModalError(t("superAdmin.messageBalance.errors.deductExceedsAvailable"));
          setSubmitting(false);
          return;
        }
        updated = await updateSuperAdminOrganizationMessageBalance(organizationId, {
          operation: "DEDUCT",
          amount: amt.toString(),
          version: balance.version,
        });
        notifications?.pushNotification({
          kind: "success",
          message: t("superAdmin.messageBalance.toasts.deductSuccess", {
            count: formatBigIntCredits(amt, i18n.language),
          }),
        });
      } else if (activeModal.type === "SET_LIMITED") {
        const amt = parseSafeNonNegativeBigInt(modalAmount);
        if (amt === null || amt < 0n) {
          setModalError(t("superAdmin.messageBalance.errors.amountMustBeNonNegative"));
          setSubmitting(false);
          return;
        }
        updated = await updateSuperAdminOrganizationMessageBalance(organizationId, {
          operation: "SET_LIMITED",
          amount: amt.toString(),
          version: balance.version,
        });
        notifications?.pushNotification({
          kind: "success",
          message: t("superAdmin.messageBalance.toasts.setLimitedSuccess"),
        });
      } else if (activeModal.type === "SET_UNLIMITED") {
        updated = await updateSuperAdminOrganizationMessageBalance(organizationId, {
          operation: "SET_UNLIMITED",
          version: balance.version,
        });
        notifications?.pushNotification({
          kind: "success",
          message: t("superAdmin.messageBalance.toasts.setUnlimitedSuccess"),
        });
      } else {
        return;
      }

      setBalance(updated);
      setActiveModal(null);
    } catch (err: any) {
      if (err?.code === "MESSAGE_BALANCE_VERSION_CONFLICT") {
        setModalError(t("superAdmin.messageBalance.errors.versionConflict"));
        void loadBalance();
      } else if (err?.code === "MESSAGE_BALANCE_MODE_CHANGE_BLOCKED") {
        setModalError(t("superAdmin.messageBalance.errors.modeChangeBlocked"));
      } else if (
        err?.code === "MESSAGE_BALANCE_INSUFFICIENT_CREDITS" ||
        err?.code === "INSUFFICIENT_MESSAGE_CREDITS"
      ) {
        setModalError(t("superAdmin.messageBalance.errors.deductExceedsAvailable"));
      } else if (err?.status === 409) {
        setModalError(t("superAdmin.messageBalance.errors.versionConflict"));
        void loadBalance();
      } else {
        setModalError(t("superAdmin.messageBalance.errors.genericUpdateFailed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !balance) {
    return (
      <section className="grid gap-3" data-testid="super-admin-message-balance-loading">
        <div className="flex items-center gap-2">
          <Coins size={16} className="text-muted" aria-hidden="true" />
          <h2 className="m-0 text-sm font-semibold text-foreground">
            {t("superAdmin.messageBalance.title")}
          </h2>
        </div>
        <div className="h-44 animate-pulse rounded-xl border border-line bg-surface" />
      </section>
    );
  }

  if (error || !balance) {
    return (
      <section className="grid gap-3" data-testid="super-admin-message-balance-error">
        <div className="flex items-center gap-2">
          <Coins size={16} className="text-muted" aria-hidden="true" />
          <h2 className="m-0 text-sm font-semibold text-foreground">
            {t("superAdmin.messageBalance.title")}
          </h2>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-line bg-surface p-4">
          <p className="m-0 text-sm text-muted">{error ?? t("superAdmin.messageBalance.loadError")}</p>
          <Button type="button" size="sm" variant="outline" onClick={() => void loadBalance()}>
            <RefreshCw size={14} className="me-1.5" aria-hidden="true" />
            {t("superAdmin.retry")}
          </Button>
        </div>
      </section>
    );
  }

  const isUnlimited = balance.isUnlimited;
  const availableBigInt = BigInt(balance.availableCredits);
  const parsedModalAmount = parseSafeNonNegativeBigInt(modalAmount);
  const previewAfterValue =
    parsedModalAmount !== null
      ? activeModal?.type === "ADD"
        ? availableBigInt + parsedModalAmount
        : activeModal?.type === "DEDUCT"
          ? availableBigInt >= parsedModalAmount
            ? availableBigInt - parsedModalAmount
            : null
          : activeModal?.type === "SET_LIMITED"
            ? parsedModalAmount
            : null
      : null;

  return (
    <section className="grid gap-4" data-testid="super-admin-message-balance-section">
      {/* Main operational card */}
      <div
        className="overflow-hidden rounded-xl border border-line bg-surface shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
        data-testid="super-admin-balance-card"
      >
        {/* Card header: Title, mode badge, rule summary & quick actions */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line/70 px-4 py-3.5 sm:px-5">
          <div className="grid min-w-0 gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <Coins size={16} className="shrink-0 text-accent" aria-hidden="true" />
              <h2 className="m-0 text-[0.9375rem] font-semibold tracking-[-0.01em] text-foreground">
                {t("superAdmin.messageBalance.title")}
              </h2>
              <Badge
                variant={isUnlimited ? "success" : "warning"}
                data-testid="super-admin-balance-mode-badge"
              >
                {isUnlimited
                  ? t("superAdmin.messageBalance.unlimitedMode")
                  : t("superAdmin.messageBalance.limitedMode")}
              </Badge>
              <BalanceInfoTooltip
                label={t("superAdmin.messageBalance.modeLabel")}
                content={t("superAdmin.messageBalance.modeTooltip")}
                testId="super-admin-balance-mode-tooltip"
              />
            </div>
            <p className="m-0 text-[0.75rem] leading-relaxed text-muted">
              {isUnlimited
                ? t("superAdmin.messageBalance.unlimitedModeDescription")
                : t("superAdmin.messageBalance.limitedModeDescription")}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Link
              to={`/super-admin/logs?organizationId=${organizationId}`}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-line/80 bg-surface px-2.5 text-xs font-medium text-muted no-underline transition-colors hover:bg-surface-muted hover:text-foreground"
              data-testid="super-admin-balance-audit-link"
            >
              <History size={13} aria-hidden="true" />
              <span>{t("superAdmin.messageBalance.viewAuditHistory")}</span>
            </Link>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="size-8 p-0"
              onClick={() => void loadBalance()}
              title={t("common.refresh")}
              aria-label={t("common.refresh")}
              data-testid="super-admin-balance-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Rule explanation strip */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line/60 bg-surface-muted/40 px-4 py-2 text-[0.75rem] text-muted sm:px-5">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground">
              {t("superAdmin.messageBalance.creditRuleSummary")}
            </span>
            <BalanceInfoTooltip
              label={t("superAdmin.messageBalance.creditRuleLabel")}
              content={t("superAdmin.messageBalance.creditRuleTooltip")}
              testId="super-admin-balance-rule-tooltip"
            />
          </div>
          <div className="text-[0.7rem] text-muted">
            {t("superAdmin.messageBalance.trackingSince")}{" "}
            <LtrValue>
              {new Date(balance.usageTrackingStartedAt).toLocaleDateString(i18n.language)}
            </LtrValue>
            <span className="mx-1.5">·</span>
            {t("superAdmin.messageBalance.version")} <LtrValue>{balance.version}</LtrValue>
          </div>
        </div>

        {/* Primary Metrics: Available (dominant) + Reserved */}
        <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-12 sm:p-5">
          {/* Dominant Metric: Available Template Messages / Unlimited status */}
          <div
            className={cn(
              "sm:col-span-4 flex flex-col justify-between rounded-xl border p-4 transition-colors",
              isUnlimited
                ? "border-success/25 bg-success-muted/20"
                : availableBigInt === 0n
                  ? "border-warning/40 bg-warning-muted/20"
                  : "border-accent/25 bg-accent-soft/25",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-foreground">
                {t("superAdmin.messageBalance.availableCredits")}
              </span>
              <BalanceInfoTooltip
                label={t("superAdmin.messageBalance.availableCredits")}
                content={
                  isUnlimited
                    ? t("superAdmin.messageBalance.unlimitedTooltip")
                    : t("superAdmin.messageBalance.availableTooltip")
                }
                testId="super-admin-balance-available-tooltip"
              />
            </div>

            <div
              className="mt-2 flex items-baseline gap-2"
              data-testid="super-admin-balance-available"
            >
              {isUnlimited ? (
                <span className="inline-flex items-center gap-2 text-lg font-semibold text-success sm:text-xl">
                  <Sparkles size={18} className="shrink-0" aria-hidden="true" />
                  <span>{t("superAdmin.messageBalance.unlimited")}</span>
                </span>
              ) : (
                <span className="text-xl font-semibold tracking-[-0.01em] text-foreground tabular-nums sm:text-2xl">
                  <LtrValue>{formatBigIntCredits(balance.availableCredits, i18n.language)}</LtrValue>
                </span>
              )}
            </div>

            <p className="m-0 mt-1.5 text-[0.75rem] leading-snug text-muted">
              {t("superAdmin.messageBalance.availableHelp")}
            </p>
          </div>

          {/* Operational Metric: Reserved Messages */}
          <div className="sm:col-span-4 flex flex-col justify-between rounded-xl border border-line/80 bg-surface-muted/35 p-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted">
                  {t("superAdmin.messageBalance.reservedCredits")}
                </span>
                <BalanceInfoTooltip
                  label={t("superAdmin.messageBalance.reservedCredits")}
                  content={t("superAdmin.messageBalance.reservedTooltip")}
                  testId="super-admin-balance-reserved-tooltip"
                />
              </div>

              <div
                className="mt-2 text-xl font-semibold tracking-[-0.01em] text-foreground tabular-nums sm:text-2xl"
                data-testid="super-admin-balance-reserved"
              >
                <LtrValue>{formatBigIntCredits(balance.reservedCredits, i18n.language)}</LtrValue>
              </div>
            </div>

            <p className="m-0 mt-1.5 text-[0.75rem] leading-snug text-muted">
              {t("superAdmin.messageBalance.reservedHelp")}
            </p>
          </div>

          {/* Operational Metric: Cumulative Send Attempts */}
          <div className="sm:col-span-4 flex flex-col justify-between rounded-xl border border-line/80 bg-surface-muted/35 p-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-muted">
                  {t("superAdmin.messageBalance.usedCredits")}
                </span>
                <BalanceInfoTooltip
                  label={t("superAdmin.messageBalance.usedCredits")}
                  content={t("superAdmin.messageBalance.usedTooltip")}
                  testId="super-admin-balance-used-tooltip"
                />
              </div>

              <div
                className="mt-2 text-xl font-semibold tracking-[-0.01em] text-foreground tabular-nums sm:text-2xl"
                data-testid="super-admin-balance-used"
              >
                <LtrValue>{formatBigIntCredits(balance.usedCredits, i18n.language)}</LtrValue>
              </div>
            </div>

            <p className="m-0 mt-1.5 text-[0.75rem] leading-snug text-muted">
              {t("superAdmin.messageBalance.usedHelp")}
            </p>
          </div>
        </div>

        {/* Action Bar: Add / Deduct + Mode Switch */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line/70 bg-surface-muted/20 px-4 py-3 sm:px-5">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={isUnlimited ? "outline" : "default"}
              disabled={isUnlimited}
              title={isUnlimited ? t("superAdmin.messageBalance.unlimitedNoAddDeduct") : undefined}
              onClick={() => handleOpenModal({ type: "ADD" })}
              data-testid="super-admin-balance-add-btn"
            >
              <Plus size={14} className="me-1" aria-hidden="true" />
              {t("superAdmin.messageBalance.addCredits")}
            </Button>

            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isUnlimited}
              title={isUnlimited ? t("superAdmin.messageBalance.unlimitedNoAddDeduct") : undefined}
              onClick={() => handleOpenModal({ type: "DEDUCT" })}
              data-testid="super-admin-balance-deduct-btn"
            >
              <Minus size={14} className="me-1" aria-hidden="true" />
              {t("superAdmin.messageBalance.deductCredits")}
            </Button>

            {isUnlimited ? (
              <span className="text-[0.75rem] text-muted">
                {t("superAdmin.messageBalance.unlimitedNoAddDeduct")}
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {isUnlimited ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-warning/45 text-warning hover:bg-warning-muted"
                onClick={() => handleOpenModal({ type: "SET_LIMITED" })}
                data-testid="super-admin-balance-set-limited-btn"
              >
                <Lock size={13} className="me-1.5" aria-hidden="true" />
                {t("superAdmin.messageBalance.switchToLimited")}
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-success/45 text-success hover:bg-success-muted"
                onClick={() => handleOpenModal({ type: "SET_UNLIMITED" })}
                data-testid="super-admin-balance-set-unlimited-btn"
              >
                <Unlock size={13} className="me-1.5" aria-hidden="true" />
                {t("superAdmin.messageBalance.switchToUnlimited")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation & Action Dialog */}
      <AlertDialog open={activeModal !== null} onOpenChange={(open) => !open && handleCloseModal()}>
        <AlertDialogContent
          className="w-[min(calc(100vw-2rem),29rem)] p-4 sm:p-6 text-start"
          data-testid="super-admin-balance-modal"
        >
          <AlertDialogTitle className="text-start">
            {activeModal?.type === "ADD" && t("superAdmin.messageBalance.modals.addTitle")}
            {activeModal?.type === "DEDUCT" && t("superAdmin.messageBalance.modals.deductTitle")}
            {activeModal?.type === "SET_LIMITED" &&
              t("superAdmin.messageBalance.modals.setLimitedTitle")}
            {activeModal?.type === "SET_UNLIMITED" &&
              t("superAdmin.messageBalance.modals.setUnlimitedTitle")}
          </AlertDialogTitle>

          <AlertDialogDescription className="text-start break-words leading-relaxed text-muted">
            {activeModal?.type === "ADD" && t("superAdmin.messageBalance.modals.addDesc")}
            {activeModal?.type === "DEDUCT" && t("superAdmin.messageBalance.modals.deductDesc")}
            {activeModal?.type === "SET_LIMITED" && (
              <>
                <span>{t("superAdmin.messageBalance.modals.setLimitedDesc")}</span>
                <span className="mt-1.5 block rounded-lg border border-line/70 bg-surface-muted/60 px-3 py-2 text-xs text-muted">
                  {t("superAdmin.messageBalance.modals.setLimitedGuardNote")}
                </span>
              </>
            )}
            {activeModal?.type === "SET_UNLIMITED" && (
              <>
                <span>{t("superAdmin.messageBalance.modals.setUnlimitedDesc")}</span>
                {availableBigInt > 0n ? (
                  <span className="mt-2 block rounded-lg border border-warning/40 bg-warning-muted/30 px-3 py-2 text-xs font-medium text-warning">
                    {t("superAdmin.messageBalance.modals.setUnlimitedForfeitWarning", {
                      count: formatBigIntCredits(balance.availableCredits, i18n.language),
                    })}
                  </span>
                ) : null}
              </>
            )}
          </AlertDialogDescription>

          {activeModal && activeModal.type !== "SET_UNLIMITED" ? (
            <div className="grid gap-3 py-2">
              <div className="grid gap-1.5">
                <label htmlFor="balance-amount-input" className="text-xs font-medium text-foreground">
                  {activeModal.type === "SET_LIMITED"
                    ? t("superAdmin.messageBalance.initialCredits")
                    : activeModal.type === "ADD"
                      ? t("superAdmin.messageBalance.creditsToAdd")
                      : t("superAdmin.messageBalance.creditsToDeduct")}
                </label>
                <input
                  id="balance-amount-input"
                  type="number"
                  min={activeModal.type === "SET_LIMITED" ? "0" : "1"}
                  dir="ltr"
                  className={cn(superAdminInputClassName, "tabular-nums")}
                  value={modalAmount}
                  onChange={(e) => {
                    setModalAmount(e.target.value);
                    setModalError(null);
                  }}
                  disabled={submitting}
                  data-testid="super-admin-balance-amount-input"
                />
                {activeModal.type === "SET_LIMITED" ? (
                  <p className="m-0 text-[0.7rem] leading-snug text-muted">
                    {t("superAdmin.messageBalance.initialCreditsHint")}
                  </p>
                ) : null}
              </div>

              {/* Quick preset buttons for ADD and SET_LIMITED */}
              {activeModal.type === "ADD" || activeModal.type === "SET_LIMITED" ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="me-1 text-[0.7rem] text-muted">
                    {t("superAdmin.messageBalance.quickAmounts")}:
                  </span>
                  {(activeModal.type === "SET_LIMITED"
                    ? QUICK_INITIAL_AMOUNTS
                    : QUICK_ADD_AMOUNTS
                  ).map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      disabled={submitting}
                      onClick={() => {
                        setModalAmount(preset);
                        setModalError(null);
                      }}
                      className={cn(
                        "rounded-md border px-2 py-0.5 text-xs font-medium tabular-nums transition-colors",
                        modalAmount === preset
                          ? "border-accent bg-accent-soft text-accent"
                          : "border-line bg-surface text-muted hover:bg-surface-muted hover:text-foreground",
                      )}
                    >
                      <LtrValue>
                        {activeModal.type === "ADD" ? "+" : ""}
                        {formatBigIntCredits(preset, i18n.language)}
                      </LtrValue>
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Live balance preview */}
              <div className="flex items-center justify-between rounded-lg border border-line/70 bg-surface-muted/50 px-3 py-2 text-xs">
                <div>
                  <span className="block text-[0.6875rem] text-muted">
                    {t("superAdmin.messageBalance.previewCurrent")}
                  </span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {isUnlimited ? (
                      t("superAdmin.messageBalance.unlimitedMode")
                    ) : (
                      <LtrValue>{formatBigIntCredits(balance.availableCredits, i18n.language)}</LtrValue>
                    )}
                  </span>
                </div>
                <ArrowRight size={14} className="text-muted rtl:-scale-x-100" aria-hidden="true" />
                <div className="text-end">
                  <span className="block text-[0.6875rem] text-muted">
                    {t("superAdmin.messageBalance.previewAfter")}
                  </span>
                  <span className="font-semibold text-foreground tabular-nums">
                    {previewAfterValue !== null ? (
                      <LtrValue>{formatBigIntCredits(previewAfterValue, i18n.language)}</LtrValue>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          {modalError ? (
            <div
              className="flex items-center gap-2 rounded-md bg-destructive/10 p-2.5 text-xs text-destructive"
              role="alert"
            >
              <AlertCircle size={15} className="shrink-0" aria-hidden="true" />
              <span>{modalError}</span>
            </div>
          ) : null}

          <AlertDialogActions>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={activeModal?.type === "SET_UNLIMITED" ? "destructive" : "default"}
              onClick={() => void handleConfirmAction()}
              disabled={submitting}
              data-testid="super-admin-balance-modal-confirm-btn"
            >
              {submitting
                ? t("common.saving")
                : activeModal?.type === "ADD"
                  ? t("superAdmin.messageBalance.modals.addConfirm")
                  : activeModal?.type === "DEDUCT"
                    ? t("superAdmin.messageBalance.modals.deductConfirm")
                    : activeModal?.type === "SET_LIMITED"
                      ? t("superAdmin.messageBalance.modals.setLimitedConfirm")
                      : activeModal?.type === "SET_UNLIMITED"
                        ? t("superAdmin.messageBalance.modals.setUnlimitedConfirm")
                        : t("common.confirm")}
            </Button>
          </AlertDialogActions>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
